import React from 'react';
import { Trash2 } from 'lucide-react';
import { useComments } from '../../hooks/useComments';
import { supabase } from '../../services/supabase';
import { TABLE_NAMES } from '../../constants';
import AddCommentForm from './AddCommentForm';
import AddPostForm from './AddPostForm';

const CommentSection = ({ latestPost, userId, isAdmin, setMessage, onAdminLogin, onPostAdded }) => {
  const { comments, loading } = useComments(latestPost?.id);

  const handleAddComment = async (name, email, text) => {
    if (!latestPost) return;
    
    // Don't allow comments on mock posts
    if (latestPost.id && latestPost.id.startsWith('mock')) {
      setMessage("Comments are not available for demo entries. Please create a real journal entry to enable comments.");
      setTimeout(() => setMessage(''), 5000);
      return;
    }
    
    try {
      const { error } = await supabase
        .from(TABLE_NAMES.BLOG_COMMENTS)
        .insert({
          post_id: latestPost.id,
          name: name,
          email: email,
          text: text,
          user_id: userId,
          created_at: new Date().toISOString(),
        });

      if (error) throw error;
      setMessage("Comment added successfully!");
    } catch (error) {
      console.error("Error adding comment: ", error);
      setMessage("Failed to add comment.");
    }
    setTimeout(() => setMessage(''), 3000);
  };

  const handleDeleteComment = async (commentId) => {
    if (!isAdmin) {
      setMessage("Error: Only admin can delete comments.");
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    try {
      const { error } = await supabase
        .from(TABLE_NAMES.BLOG_COMMENTS)
        .delete()
        .eq('id', commentId);

      if (error) throw error;
      setMessage("Comment deleted.");
    } catch (error) {
      console.error("Error deleting comment: ", error);
      setMessage("Failed to delete comment.");
    }
    setTimeout(() => setMessage(''), 3000);
  };

  if (loading) {
    return (
      <section className="mt-10 pt-6 border-t border-gray-200">
        <div className="text-center text-gray-500">Loading comments...</div>
      </section>
    );
  }

  return (
    <section className="mt-10 pt-6 border-t border-gray-200 pdf-exclude" data-pdf-exclude="true">
      <h3 className="font-serif text-2xl font-semibold tracking-tight text-stone-900 mb-5">{comments.length} Comments</h3>

      <AddCommentForm onAddComment={handleAddComment} />

      <div className="mt-8 space-y-6">
        {comments.length > 0 ? (
          comments.map(comment => (
            <div key={comment.id} className="p-4 bg-card rounded-xl border border-line animate-slide-up">
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <p className="font-semibold text-gray-900">{comment.name}</p>
                  <p className="text-xs text-gray-500">
                    {comment.created_at ? new Date(comment.created_at).toLocaleString() : 'Just now'}
                  </p>
                </div>
                {isAdmin && (
                  <button
                    onClick={() => handleDeleteComment(comment.id)}
                    className="p-1 text-red-500 hover:text-red-700 transition"
                    title="Delete Comment (Admin Only)"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="mt-3 text-gray-700">{comment.text}</p>
            </div>
          ))
        ) : (
          <p className="text-gray-500 italic">Be the first to leave a comment!</p>
        )}
      </div>

      {/* Admin Blog Post Creation - Below Comments */}
      {isAdmin && (
        <div className="mt-8 pt-6 border-t border-gray-200">
          <AddPostForm 
            isAdmin={isAdmin} 
            authorId={userId} 
            setMessage={setMessage}
            onPostAdded={onPostAdded}
          />
        </div>
      )}
    </section>
  );
};

export default CommentSection;
